import { Check } from '@mui/icons-material';
import { CircularProgress, Fade, Stack, SvgIcon } from '@mui/material';
import { useMethodGuide } from 'api/guide';
import { useCurrentIdP } from 'api/idps';
import IdPChip from 'components/shared/IdPChip';
import SidebarMenuItem from 'components/SidebarMenu/SidebarMenuItem';
import SidebarMenuSection from 'components/SidebarMenu/SidebarMenuSection';
import { ROUTES } from 'constants/routes';
import { GuideType } from 'consts';
import { useSSOGuideProgress } from 'contexts/GuideProgressContext';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useNav, useStepNavigate, useTestingNavigate } from 'utils/navigation';

interface ISSOMenuSectionProps {
	open: boolean;
	toggleOpen: () => void;
}

const SSOMenuSection: React.FC<ISSOMenuSectionProps> = ({
	open,
	toggleOpen
}) => {
	const nav = useNav();
	const location = useLocation();

	const { guideStepNum, method, idpID, guideType } = useIdPConfigContext();

	const { data: currentIdPData } = useCurrentIdP();
	const { data: currentIdPGuideData, isLoading: isLoadingIdpGuideData } =
		useMethodGuide(idpID, method);
	const { navigateToTesting } = useTestingNavigate();
	const { navigateToStep } = useStepNavigate();

	const { isStepCompleted, completedSteps, totalSteps, ssoTested } =
		useSSOGuideProgress();

	return (
		<SidebarMenuSection
			title={intl.tr('general.SSOConfiguration')}
			open={open}
			onTitleItemClick={toggleOpen}
			progress={completedSteps}
			progressTotal={totalSteps}
			startIcon={<SvgIcon fontSize="small" />}
			testIdTopic="sso"
		>
			<SidebarMenuItem
				data-testid="select-idp-menu-item"
				active={Boolean(matchPath(ROUTES.selectIdP, location.pathname))}
				buttonBaseProps={{ onClick: () => nav(ROUTES.selectIdP) }}
				startIcon={
					<Check
						sx={{
							width: 20,
							visibility: currentIdPData ? 'visible' : 'hidden'
						}}
						fontSize="small"
						color="primary"
					/>
				}
				endIcon={currentIdPData && <IdPChip idp={currentIdPData} />}
			>
				{intl.tr('ssoConfig.guide.selectIdP.pageTitle')}
			</SidebarMenuItem>
			{currentIdPGuideData?.guide.map((step, i) => (
				<Fade key={step.title} in style={{ transitionDelay: `${i * 75}ms` }}>
					<Stack width="100%">
						<SidebarMenuItem
							data-testid="guide-step-menu-item"
							active={
								guideType === GuideType.SSO &&
								Boolean(matchPath(ROUTES.guideStep, location.pathname)) &&
								guideStepNum === i + 1
							}
							buttonBaseProps={{ onClick: () => navigateToStep(i + 1) }}
							startIcon={
								<Check
									sx={{
										width: 20,
										visibility: isStepCompleted(i + 1) ? 'visible' : 'hidden'
									}}
									fontSize="small"
									color="primary"
								/>
							}
						>
							{step.title}
						</SidebarMenuItem>
					</Stack>
				</Fade>
			))}
			{currentIdPGuideData?.guide.length && (
				<Fade
					in
					style={{
						transitionDelay: `${currentIdPGuideData.guide.length * 75}ms`
					}}
				>
					<Stack width="100%">
						<SidebarMenuItem
							data-testid="testing-menu-item"
							active={Boolean(matchPath(ROUTES.testing, location.pathname))}
							buttonBaseProps={{ onClick: navigateToTesting }}
							startIcon={
								<Check
									sx={{
										width: 20,
										visibility: ssoTested ? 'visible' : 'hidden'
									}}
									fontSize="small"
									color="primary"
								/>
							}
						>
							{intl.tr('ssoConfig.testPage.pageTitle')}
						</SidebarMenuItem>
					</Stack>
				</Fade>
			)}
			{isLoadingIdpGuideData && (
				<Stack alignItems="center">
					<CircularProgress size={20} />
				</Stack>
			)}
		</SidebarMenuSection>
	);
};

export default SSOMenuSection;
